import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReducersTypes } from "@/redux/reducers/Models";
import RegisterPinView from "./view";
import { sendPinRegister, sendPinAvulso, setCustomer } from "@/redux/ducks/customer";
import usePinForm from "../hooks/usePinForm";
import usePinFormAvulso from "../hooks/usePinFormAvulso";
import { useSingleFlag } from "@/hooks/useFlags";


export default function RegisterPin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes & {
    userReducer: {
      pin: string;
    }
  }) => state.customerReducer);
  const user = useSelector((state: ReducersTypes) => state.userReducer);
  const TIME_AGAIN = 1000;

  const { id } = useParams();

  const isAvulsoFlow = !customer?.register?.document && user?.plate && (user?.name || user?.email || user?.phone);

  // Feature flags Avulso
  const { isEnabled: showAvulsoSms, isLoading: isLoadingAvulsoSms } = useSingleFlag('PD_PIN_AVULSO_SMS');
  const { isEnabled: showAvulsoWhatsApp, isLoading: isLoadingAvulsoWhatsApp } = useSingleFlag('PD_PIN_AVULSO_WHATSAPP');
  const { isEnabled: showAvulsoEmail, isLoading: isLoadingAvulsoEmail } = useSingleFlag('PD_PIN_AVULSO_EMAIL');

  // Feature flags Onboarding
  const { isEnabled: showOnboardingSms, isLoading: isLoadingOnboardingSms } = useSingleFlag('PD_PIN_ONBOARDING_SMS');
  const { isEnabled: showOnboardingEmail, isLoading: isLoadingOnboardingEmail } = useSingleFlag('PD_PIN_ONBOARDING_EMAIL');

  const isLoadingFlags = isAvulsoFlow
    ? isLoadingAvulsoSms || isLoadingAvulsoWhatsApp || isLoadingAvulsoEmail
    : isLoadingOnboardingSms || isLoadingOnboardingEmail;

  const isChannelAllowed =
    (id === "phone" && (isAvulsoFlow ? showAvulsoSms : showOnboardingSms)) ||
    (id === "email" && (isAvulsoFlow ? showAvulsoEmail : showOnboardingEmail)) ||
    (id === "whatsapp" && isAvulsoFlow && showAvulsoWhatsApp);

  const pinFormHookAvulso = usePinFormAvulso(navigate);
  const pinFormHookRegister = usePinForm(navigate);
  
  const pinFormHook = isAvulsoFlow ? pinFormHookAvulso : pinFormHookRegister;
  
  const {
      toggleModal,
      isVisibleModal,
      isLoading: isLoadingForm,
      isDisabled,
      error,
      register,
      setValue,
      handleSubmit,
      errors,
  } = pinFormHook;

  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [pinAgain, setPinAgain] = useState(false);

  const goBack = () => {
    navigate(-1);
  };

  const goToLogin = () => navigate("/?auth=true");

  const handleSendPin = () => {
    if (isLoading) return;
    
    setIsLoading(true);
    
    if (isAvulsoFlow) {
      const channelTypeMap = {
        whatsapp: "whatsapp",
        sms: "sms",
        email: "email",
      } as const;  

      const channelType = channelTypeMap[id as keyof typeof channelTypeMap];

      dispatch(
        sendPinAvulso({
          plate: user?.plate as string,
          phone: user?.phone as string,
          email: user?.email as string,
          channelType: channelType as keyof typeof channelTypeMap,
          name: user?.name as string,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          callback: _ => {
            setIsLoading(false);
          }
        })
      );
    } else {
      dispatch(
        sendPinRegister({
          document: customer?.register?.document!,
          phone: id === "phone" ? customer?.register?.phone! : undefined,
          email: id === "email" ? customer?.register?.email! : undefined,
          name: customer?.register?.name!,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          callback: _ => {
            setIsLoading(false);
          }
        })
      );
    }
  };

  const handleTryAgain = () => {
    if (timer !== 0 || isLoading) return;
    setIsLoading(true);
    setTimer(30);
    handleSendPin();
  }

  const updateCount = useCallback(() => {
    if (timer > 0) {
      setTimeout(() => {
        setTimer(timer - 1);
      }, TIME_AGAIN);
    }
  }, [timer]);

  useEffect(() => {
    if (timer !== 0) {
      updateCount();
      if (!pinAgain) setPinAgain(true);
    } else if (pinAgain) setPinAgain(false);
  }, [timer, setPinAgain, updateCount]);

  useEffect(() => {
    if (isLoadingFlags) return;
    if (!isChannelAllowed) {
      navigate('/register/choosepin');
      return;
    }
    dispatch(setCustomer({register: {
      ...customer?.register,
      pin: "",
      hashCode: "",
    } }));
    handleSendPin();
    updateCount();
  }, [isLoadingFlags, isChannelAllowed]);

  return (
    <RegisterPinView 
      handleTryAgain={handleTryAgain}
      timer={timer}
      isLoading={isLoading || isLoadingForm || isLoadingFlags}
      handleSubmit={handleSubmit}
      goBack={goBack}
      isDisabled={isDisabled}
      isVisibleModal={isVisibleModal}
      toggleModal={toggleModal}
      goToLogin={goToLogin}
      isEmail={id === "email"}
      isWhatsApp={id === "whatsapp"}
      register={register}
      setValue={setValue}
      errors={errors}
      error={error}
    />
  );
}
